import {
    GET_FORM_WITH_MAIN_SECTIONS,
    GET_ENTITY_TYPE_ASSIGNED_QUESTIONS,
    CREATE_CHECKLIST_ITEM_ASSOCIATION,
    GET_CHECKLIST_ITEM_ASSOCIATIONS,
    UNASSIGN_CHECKLIST_ITEM_ASSOCIATION,
    get_token,
    MANAGE_CHECKLIST_REQUIREMENT_TYPES,
    CHECK_TARGET_QUESTION_REQUIREMENTS,
    GET_CHECKLIST_ITEM_CONDITION,
    MANAGE_CHECKLIST_ITEM_CONDITION,
    GET_COMPLIANCE_THRESHOLDS,
    MANAGE_COMPLIANCE_THRESHOLDS,
    REQUIREMENT_TYPE_ANSWERED,
    REQUIREMENT_TYPE_MANUAL_VERIFICATION,
    REQUIREMENT_TYPE_AUTO_VERIFICATION,
    REQUIREMENT_TYPE_REQUESTED_INFO,
    GET_REQUIREMENT_TYPES
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import question_form_mixin from "@/utils/mixins/question_form_mixin";
import axios from "axios";


export default {
    name: "ComplianceChecklist",
    mixins: [
        alert_mixin,
        formatting_mixins,
        question_form_mixin
      ],
    data: () => ({
        token: get_token(),
        form: null,
        form_id: null,
        loading: true,
        show_assign_modal: false,
        selected_question: null,
        selected_section_name: "",
        new_target_question: {
            question_text: "",
            question_type: "text",
            is_mandatory: false,
        },
        open_sections: [],
        question_list: [],
        selected_question_text: "",
        question_search: "",
        is_loading_questions: false,
        entity_type_id: null,
        association_id_by_target_id: new Map(),
        assigned_target_ids: [],
        selected_form_id: null,
        selected_section_id: null,
        selected_target_question_id: null,
        requirement_types: [
            { id: 1, name: REQUIREMENT_TYPE_ANSWERED },
            { id: 2, name: REQUIREMENT_TYPE_MANUAL_VERIFICATION },
            { id: 3, name: REQUIREMENT_TYPE_AUTO_VERIFICATION },
            { id: 4, name: REQUIREMENT_TYPE_REQUESTED_INFO }
        ],
        selected_requirement_types: {},
        condition_context_question: null,
        condition_selected_target_id: null,
        condition_loading: false,
        condition_saving: false,
        condition_check_mode: "automatic",
        condition_selected_requirement_types: [],
        condition_target_options: [],
        condition_selected_option_ids: [],
        condition_number_min: null,
        condition_number_max: null,
        condition_date_min: null,
        condition_date_max: null,
        condition_char_min: null,
        condition_char_max: null,
        condition_document_submission_expected: null,
        viewing_conditions: [],
        viewing_conditions_loading: false,
        threshold_loading: false,
        threshold_saving: false,
        threshold_values: {
            qualified_start_percent: 80,
            qualified_end_percent: 100,
            partially_qualified_start_percent: 50,
            partially_qualified_end_percent: 79.99,
            unqualified_start_percent: 0,
            unqualified_end_percent: 49.99,
        },
    }),
    created() {
        this.form_id = this.$route.query.form_id;
        this.entity_type_id = sessionStorage.getItem("entity_type_id");
        this.get_assigned_questions();
    },
    computed: {
        headers() {
            const tokenData = {
                "Content-Type": "application/json",
                Authorization: this.token ? `Token ${this.token}` : "",
            };
            return tokenData;
        },
        filtered_questions() {
            let questions = Array.isArray(this.question_list)
                ? this.question_list
                : [];

            if (this.selected_form_id) {
                questions = questions.filter(
                    section_question => section_question.form?.form_id === Number(this.selected_form_id)
                );
            }

            if (this.selected_section_id) {
                questions = questions.filter(
                    section_question => section_question.section?.section_id === Number(this.selected_section_id)
                );
            }

            const search_text = (this.question_search || "").trim().toLowerCase();
            if (search_text) {
                questions = questions.filter(section_question => {
                    const text =
                        section_question.question?.question_text ||
                        section_question.question_text ||
                        "";
                    return text.toLowerCase().includes(search_text);
                });
            }

            return questions;
        },

        available_forms() {
            const forms = new Map();
            this.question_list.forEach(item => {
                if (item.form) {
                    forms.set(item.form.form_id, item.form.form_name);
                }
            });
            return Array.from(forms, ([id, name]) => ({ id, name }));
        },

        available_sections() {
            if (!this.selected_form_id) return [];

            const sections = new Map();
            this.question_list.forEach(item => {
                if (item.form?.form_id === Number(this.selected_form_id) && item.section) {
                    sections.set(item.section.section_id, item.section.section_name);
                }
            });

            return Array.from(sections, ([id, name]) => ({ id, name }));
        },

        available_questions() {
            if (!this.selected_section_id) return [];

            return this.question_list.filter(item =>
                item.section?.section_id === Number(this.selected_section_id)
            );
        },

    },

    methods: {
        get_form_sections_with_sections() {
            this.loading = true;

            axios.get(GET_FORM_WITH_MAIN_SECTIONS, {
                headers: this.headers,
                params: { form_id: this.form_id },
            })
                .then(async (response) => {
                    const resp = response.data;
                    this.form = this.normalize_form_response(resp);
                    this.ensure_question_defaults(this.form);
                    await this.fetch_all_target_questions_for_form();

                    if (this.form.length > 0 && this.form[0].sub_sections?.length > 0) {
                        this.open_sections = [this.form[0].sub_sections[0].section_id];
                    }
                })
                .catch((error) => {
                    this.show_error("Error fetching form sections: " + error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        normalize_form_response(resp) {
            if (Array.isArray(resp)) return resp;
            if (Array.isArray(resp?.data)) return resp.data;
            if (Array.isArray(resp?.main_sections)) return resp.main_sections;
            return [];
        },

        ensure_question_defaults(form) {
            if (!Array.isArray(form)) return;
            form.forEach((main_section) => {
                const sub_sections = Array.isArray(main_section.sub_sections) ? main_section.sub_sections : [];
                sub_sections.forEach((sub_section) => {
                    const questions = Array.isArray(sub_section.questions) ? sub_section.questions : [];
                    questions.forEach((question) => {
                        if (!question.target_questions) {
                            if (typeof this.$set === "function") {
                                this.$set(question, "target_questions", []);
                            } else {
                                question.target_questions = [];
                            }
                        }

                        if (!Array.isArray(question.dependent_questions)) {
                            question.dependent_questions = [];
                        }
                        if (typeof question.is_assigned === "undefined") {
                            question.is_assigned = false;
                        }
                        if (typeof question.is_loading === "undefined") {
                            question.is_loading = false;
                        }
                    });
                });
            });
        },
        get_assigned_questions() {
            this.is_loading_questions = true;

            axios.get(GET_ENTITY_TYPE_ASSIGNED_QUESTIONS, {
                headers: this.headers,
                params: { entity_type_id: this.entity_type_id },
            })
                .then((response) => {
                    const raw = response.data.data || [];
                    this.question_list = raw.map(item => ({
                        ...item,
                        dependent_questions: Array.isArray(item.dependent_questions) ? item.dependent_questions : [],
                        target_questions: Array.isArray(item.target_questions) ? item.target_questions : [],
                        is_assigned: typeof item.is_assigned === "undefined" ? false : item.is_assigned,
                        is_loading: typeof item.is_loading === "undefined" ? false : item.is_loading,
                    }));
                })
                .catch((error) => {
                    this.show_error("Error fetching assigned questions: " + error.message);
                })
                .finally(() => {
                    this.is_loading_questions = false;
                });
        },

        toggle_section(section_id) {
            const index = this.open_sections.indexOf(section_id);
            if (index > -1) {
                // Section is open, close it
                this.open_sections.splice(index, 1);
            } else {
                // Section is closed, open it
                this.open_sections.push(section_id);
            }
        },
        is_section_open(section_id) {
            return this.open_sections.includes(section_id);
        },

        set_selected_question(section_question, section_name) {
            this.selected_question = section_question;
            this.selected_section_name = section_name;

            this.selected_question_text =
                section_question?.question_text ||
                section_question?.question?.question_text ||
                "Selected question";

            this.question_search = "";

            // Load target questions already assigned to this checklist item
            const checklist_item_id =
                section_question?.section_question_id ||
                section_question?.id;

            if (checklist_item_id) {
                this.load_checklist_item_associations(checklist_item_id);
            }
        },

        close_assign_modal() {
            this.show_assign_modal = false;
            this.selected_question = null;
            this.selected_section_name = "";
        },
        get_section_question_id(item) {
            return (
                item?.section_question_id ||
                item?.question?.section_question_id ||
                item?.id
            );
        },

        load_checklist_item_associations(checklist_item_id) {
            // reset maps
            this.association_id_by_target_id = new Map();
            this.assigned_target_ids = [];

            // Load requirement types for this checklist item
            axios.get(GET_REQUIREMENT_TYPES, {
                headers: this.headers,
                params: { checklist_item_id }
            })
                .then((response) => {
                    if (response.data.status === 'success') {
                        const requirement_types = response.data.data || [];
                        Reflect.set(this.selected_requirement_types, checklist_item_id, requirement_types);
                    }
                })
                .catch(() => {
                    // Initialize with empty array if error
                    Reflect.set(this.selected_requirement_types, checklist_item_id, []);
                });

            return axios.get(GET_CHECKLIST_ITEM_ASSOCIATIONS, {
                headers: this.headers,
                params: { checklist_item_id },
            })
                .then((response) => {
                    const payload = response.data || {};
                    const associations = payload.associations || payload.data || [];

                    // Build lookup map safely
                    associations.forEach((a) => {
                        const target_id = Number(a.target_question_id);
                        const assoc_id = a.association_id || a.id;

                        if (Number.isInteger(target_id) && assoc_id) {
                            this.association_id_by_target_id.set(target_id, assoc_id);
                        }
                    });

                    this.assigned_target_ids = Array.from(
                        this.association_id_by_target_id.keys()
                    );

                    // Update selected_question.target_questions UI list
                    if (this.selected_question) {
                        if (!Array.isArray(this.selected_question.target_questions)) {
                            this.selected_question.target_questions = [];
                        }

                        this.selected_question.target_questions = associations.map((a) => ({
                            id: Number(a.target_question_id),
                            question_text: a.target_question_text,
                            question_type: a.target_question_type,
                            is_mandatory: a.target_question_is_mandatory,
                            association_id: a.association_id || a.id,
                        }));
                    }

                    this.sync_modal_toggle_state();
                })
                .catch((error) => {
                    this.show_error("Error loading target questions: " + error.message);
                });
        },


        sync_modal_toggle_state() {
            const assigned = new Set(this.assigned_target_ids);

            this.question_list = (Array.isArray(this.question_list) ? this.question_list : []).map((item) => {
                const id = this.get_section_question_id(item);
                return {
                    ...item,
                    is_assigned: assigned.has(Number(id)),
                    is_loading: false,
                };
            });
        },

        create_association_api(target_question_id, checklist_item_id) {
            return axios.post(
                CREATE_CHECKLIST_ITEM_ASSOCIATION,
                {
                    target_question_id,
                    checklist_item_id,
                    operator: "not_empty",
                },
                { headers: this.headers }
            );
        },

        unassign_association_api(target_question_id, checklist_item_id) {
            const safe_target_id = Number(target_question_id);

            const association_id = Number.isInteger(safe_target_id)
                ? this.association_id_by_target_id.get(safe_target_id)
                : null;

            const payload = association_id
                ? { association_id }
                : {
                    target_question_id: safe_target_id,
                    checklist_item_id: Number(checklist_item_id),
                };

            return axios.post(UNASSIGN_CHECKLIST_ITEM_ASSOCIATION, payload, {
                headers: this.headers,
            });
        },



        async on_toggle_assign(event, section_question) {
            if (!this.selected_question) return;

            const checklist_item_id =
                this.selected_question.section_question_id ||
                this.selected_question.id;

            const target_question_id = this.get_section_question_id(section_question);

            const is_checked = event?.target?.checked === true;

            section_question.is_loading = true;

            const req = is_checked
                ? this.create_association_api(target_question_id, checklist_item_id)
                : this.unassign_association_api(target_question_id, checklist_item_id);

            req.then(() => {
                // reload associations so UI stays consistent
                return this.load_checklist_item_associations(checklist_item_id);
            })
                .catch((error) => {
                    this.show_error("Failed to update assignment: " + error.message);
                })
                .finally(() => {
                    section_question.is_loading = false;
                });
        },

        async remove_target_question(question, target_q) {
            const checklist_item_id =
                question?.section_question_id ||
                this.selected_question?.section_question_id ||
                question?.id ||
                this.selected_question?.id;

            const target_question_id = target_q?.id;

            if (!checklist_item_id || !target_question_id) return;

            question.target_questions = question.target_questions.filter(
                section_question => section_question.id !== target_question_id
            );

            this.unassign_association_api(target_question_id, checklist_item_id)
                .then(() => {
                    // Optional: re-sync from backend to be 100% safe
                    this.load_checklist_item_associations(checklist_item_id);
                })
                .catch((error) => {
                    this.show_error("Failed to remove target question: " + error.message);

                    this.load_checklist_item_associations(checklist_item_id);
                });
        },

        is_select_target_question_type(question_type) {
            return ["single_select", "multi_select"].includes(question_type);
        },

        is_number_target_question_type(question_type) {
            return ["number", "integer", "float", "decimal", "currency"].includes(question_type);
        },

        is_date_target_question_type(question_type) {
            return ["date", "datetime"].includes(question_type);
        },

        is_character_target_question_type(question_type) {
            return [
                "short_text",
                "long_text",
                "text",
                "textarea",
                "email",
                "phone",
                "tel",
                "url",
            ].includes(question_type);
        },

        is_document_target_question_type(question_type) {
            return ["file", "file_upload", "facial_verification"].includes(question_type);
        },

        get_selected_target_question_for_condition() {
            const targets = this.condition_context_question?.target_questions || [];
            return targets.find(
                (target) => Number(target.id) === Number(this.condition_selected_target_id)
            );
        },

        async open_add_condition_modal(question) {
            const targets = Array.isArray(question?.target_questions)
                ? question.target_questions
                : [];

            if (!targets.length) {
                this.show_error("Assign at least one target question before adding conditions.");
                return;
            }

            this.condition_context_question = question;
            this.condition_selected_target_id = targets[0].id;
            await this.load_condition_for_target();
        },

        async on_condition_target_change() {
            await this.load_condition_for_target();
        },

        async load_condition_for_target() {
            const target = this.get_selected_target_question_for_condition();
            if (!target?.association_id) {
                this.condition_selected_requirement_types = [];
                this.condition_target_options = [];
                this.condition_selected_option_ids = [];
                this.condition_check_mode = "automatic";
                this.condition_number_min = null;
                this.condition_number_max = null;
                this.condition_date_min = null;
                this.condition_date_max = null;
                this.condition_char_min = null;
                this.condition_char_max = null;
                this.condition_document_submission_expected = null;
                return;
            }

            this.condition_loading = true;

            try {
                const response = await axios.get(GET_CHECKLIST_ITEM_CONDITION, {
                    headers: this.headers,
                    params: { checklist_item_association_id: target.association_id },
                });

                const data = response.data?.data || {};
                this.condition_selected_requirement_types = Array.isArray(data.requirement_type_names)
                    ? data.requirement_type_names
                    : [];
                this.condition_check_mode = data.check_mode || "automatic";
                this.condition_target_options = Array.isArray(data.target_question_options)
                    ? data.target_question_options
                    : [];
                this.condition_selected_option_ids = Array.isArray(data.selected_option_ids)
                    ? data.selected_option_ids
                    : [];
                this.condition_number_min = data.number_min;
                this.condition_number_max = data.number_max;
                this.condition_date_min = data.date_min;
                this.condition_date_max = data.date_max;
                this.condition_char_min = data.char_min;
                this.condition_char_max = data.char_max;
                this.condition_document_submission_expected =
                    this.normalize_document_submission_expected(
                        data.document_submission_expected
                    );
            } catch (error) {
                this.show_error("Failed to load condition: " + (error.response?.data?.message || error.message));
            } finally {
                this.condition_loading = false;
            }
        },

        normalize_condition_bound(value) {
            return value === "" || typeof value === "undefined" ? null : value;
        },

        normalize_document_submission_expected(value) {
            if (value === null || typeof value === "undefined" || value === "") {
                return null;
            }

            if (typeof value === "boolean") {
                return value;
            }

            const normalized = String(value).trim().toLowerCase();
            if (["true", "1", "yes"].includes(normalized)) {
                return true;
            }
            if (["false", "0", "no"].includes(normalized)) {
                return false;
            }

            return null;
        },

        build_condition_payload(association_id) {
            const payload = {
                checklist_item_association_id: association_id,
                requirement_type_names: this.condition_selected_requirement_types,
                check_mode: this.condition_check_mode,
                selected_option_ids: this.condition_selected_option_ids,
                number_min: this.normalize_condition_bound(this.condition_number_min),
                number_max: this.normalize_condition_bound(this.condition_number_max),
                date_min: this.normalize_condition_bound(this.condition_date_min),
                date_max: this.normalize_condition_bound(this.condition_date_max),
                char_min: this.normalize_condition_bound(this.condition_char_min),
                char_max: this.normalize_condition_bound(this.condition_char_max),
                document_submission_expected: this.normalize_document_submission_expected(
                    this.condition_document_submission_expected
                ),
            };
            return payload;
        },

        async save_current_condition() {
            const target = this.get_selected_target_question_for_condition();
            if (!target?.association_id) {
                this.show_error("No target association selected.");
                return;
            }

            this.condition_saving = true;

            try {
                await axios.post(
                    MANAGE_CHECKLIST_ITEM_CONDITION,
                    this.build_condition_payload(target.association_id),
                    { headers: this.headers }
                );

                const checklist_item_id =
                    this.condition_context_question?.section_question_id ||
                    this.condition_context_question?.id;
                if (checklist_item_id) {
                    await this.load_requirement_types_for_checklist_item(checklist_item_id);
                }

                this.show_success("Condition saved successfully.");
            } catch (error) {
                this.show_error("Failed to save condition: " + (error.response?.data?.message || error.message));
            } finally {
                this.condition_saving = false;
            }
        },

        async open_view_conditions_modal(question) {
            this.condition_context_question = question;
            this.viewing_conditions = [];
            this.viewing_conditions_loading = true;

            try {
                const targets = Array.isArray(question?.target_questions)
                    ? question.target_questions
                    : [];

                const requests = targets
                    .filter((target) => !!target.association_id)
                    .map((target) =>
                        axios
                            .get(GET_CHECKLIST_ITEM_CONDITION, {
                                headers: this.headers,
                                params: {
                                    checklist_item_association_id: target.association_id,
                                },
                            })
                            .then((response) => ({
                                target_question_text: target.question_text,
                                target_question_type: target.question_type,
                                ...(response.data?.data || {}),
                                document_submission_expected:
                                    this.normalize_document_submission_expected(
                                        response.data?.data?.document_submission_expected
                                    ),
                            }))
                    );

                this.viewing_conditions = await Promise.all(requests);
            } catch (error) {
                this.show_error("Failed to load conditions: " + (error.response?.data?.message || error.message));
            } finally {
                this.viewing_conditions_loading = false;
            }
        },

        fetch_all_target_questions_for_form() {
            const checklist_item_ids = [];

            (this.form || []).forEach((main_section) => {
                (main_section.sub_sections || []).forEach((sub_section) => {
                    (sub_section.questions || []).forEach((section_question) => {
                        const id = section_question.section_question_id || section_question.id;
                        if (id) checklist_item_ids.push(id);
                    });
                });
            });

            // De-dupe
            const unique_ids = Reflect.apply(Array.from, Array, [new Set(checklist_item_ids)]);

            // Fetch associations for each checklist item and attach to that question
            const requests = unique_ids.map((checklist_item_id) =>
                axios.get(GET_CHECKLIST_ITEM_ASSOCIATIONS, {
                    headers: this.headers,
                    params: { checklist_item_id },
                })
                    .then((res) => {
                        const associations = res.data?.associations || [];

                        // Convert association response to your UI structure
                        const targets = associations.map((a) => ({
                            id: a.target_question_id,
                            question_text: a.target_question_text || a.target_question || "",
                            question_type: a.target_question_type || "",
                            is_mandatory: a.target_question_is_mandatory ?? false,
                            association_id: a.association_id || a.id,
                            requirement_types: a.requirement_types || [],
                        }));

                        // Find the question in this.form and set target_questions
                        this.attach_targets_to_question(checklist_item_id, targets);
                    })
            );

            return Promise.all(requests);
        },

        attach_targets_to_question(checklist_item_id, targets) {
            (this.form || []).forEach((main_section) => {
                (main_section.sub_sections || []).forEach((sub_section) => {
                    (sub_section.questions || []).forEach((section_question) => {
                        const id = section_question.section_question_id || section_question.id;
                        if (Number(id) === Number(checklist_item_id)) {
                            if (typeof this.$set === "function") {
                                this.$set(section_question, "target_questions", targets);
                            } else {
                                section_question.target_questions = targets;
                            }
                        }
                    });
                });
            });

            // Load requirement types for this checklist item separately
            this.load_requirement_types_for_checklist_item(checklist_item_id);
        },

        async load_requirement_types_for_checklist_item(checklist_item_id) {
            const response = await axios.get(GET_REQUIREMENT_TYPES, {
                headers: this.headers,
                params: { checklist_item_id }
            });

            const requirement_types = response.data?.status === 'success' ? (response.data.data || []) : [];
            Reflect.set(this.selected_requirement_types, checklist_item_id, requirement_types);
        },

        async check_target_question_requirements(target_question_id, entity_profile_id) {
            try {
                const response = await axios.post(CHECK_TARGET_QUESTION_REQUIREMENTS, {
                    target_question_id,
                    entity_profile_id
                }, {
                    headers: this.headers
                });

                if (response.data.status === 'success') {
                    return response.data.data;
                }
            } catch (error) {
                this.show_error('Error checking requirements: ' + error.message);
                return null;
            }
        },

        async toggle_requirement_type(question, requirement_type_name) {
            const question_id = question.section_question_id || question.id;

            if (!Reflect.get(this.selected_requirement_types, question_id)) {
                Reflect.set(this.selected_requirement_types, question_id, []);
            }

            const current_types = Reflect.get(this.selected_requirement_types, question_id);
            const index = current_types.indexOf(requirement_type_name);
            if (index > -1) {
                current_types.splice(index, 1);
            } else {
                current_types.push(requirement_type_name);
            }

            // Save immediately to backend
            await this.save_requirement_types(question);
        },

        is_requirement_type_selected(question, requirement_type_name) {
            const question_id = question.section_question_id || question.id;
            const types = Reflect.get(this.selected_requirement_types, question_id);
            return types?.includes(requirement_type_name) || false;
        },


        async save_requirement_types(question) {
            const question_id = question.section_question_id || question.id;
            const selected_type_names = Reflect.get(this.selected_requirement_types, question_id) || [];

            // Send checklist_item_id (the main question ID) to backend
            if (!question_id) {
                return; // Silently return if no question ID
            }

            try {
                await axios.post(MANAGE_CHECKLIST_REQUIREMENT_TYPES, {
                    checklist_item_id: question_id,
                    requirement_type_names: selected_type_names
                }, {
                    headers: this.headers
                });

                await this.fetch_all_target_questions_for_form();
            } catch (error) {
                this.show_error('Error saving requirement types: ' + (error.response?.data?.message || error.message));
            }
        },

        async open_threshold_modal() {
            await this.load_compliance_thresholds();
        },

        async load_compliance_thresholds() {
            if (!this.form_id) {
                return;
            }

            this.threshold_loading = true;
            try {
                const response = await axios.get(GET_COMPLIANCE_THRESHOLDS, {
                    headers: this.headers,
                    params: {
                        form_id: Number(this.form_id),
                        entity_type_id: this.entity_type_id ? Number(this.entity_type_id) : undefined,
                    },
                });

                const data = response?.data?.data || {};
                this.threshold_values = {
                    qualified_start_percent: Number(data.qualified_start_percent ?? 80),
                    qualified_end_percent: Number(data.qualified_end_percent ?? 100),
                    partially_qualified_start_percent: Number(
                        data.partially_qualified_start_percent ?? 50
                    ),
                    partially_qualified_end_percent: Number(
                        data.partially_qualified_end_percent ?? 79.99
                    ),
                    unqualified_start_percent: Number(data.unqualified_start_percent ?? 0),
                    unqualified_end_percent: Number(data.unqualified_end_percent ?? 49.99),
                };
            } catch (error) {
                this.show_error(
                    "Failed to load thresholds: " + (error.response?.data?.message || error.message)
                );
            } finally {
                this.threshold_loading = false;
            }
        },

        async save_thresholds() {
            if (!this.form_id) {
                this.show_error("Form is missing for threshold settings.");
                return;
            }

            const payload = {
                form_id: Number(this.form_id),
                entity_type_id: this.entity_type_id ? Number(this.entity_type_id) : undefined,
                qualified_start_percent: Number(this.threshold_values.qualified_start_percent),
                qualified_end_percent: Number(this.threshold_values.qualified_end_percent),
                partially_qualified_start_percent: Number(this.threshold_values.partially_qualified_start_percent),
                partially_qualified_end_percent: Number(this.threshold_values.partially_qualified_end_percent),
                unqualified_start_percent: Number(this.threshold_values.unqualified_start_percent),
                unqualified_end_percent: Number(this.threshold_values.unqualified_end_percent),
            };

            if (Object.values(payload).some(value => Number.isNaN(value))) {
                this.show_error("Thresholds must be valid numbers.");
                return;
            }

            this.threshold_saving = true;
            try {
                const response = await axios.post(
                    MANAGE_COMPLIANCE_THRESHOLDS,
                    payload,
                    { headers: this.headers }
                );

                if (response.data?.status === "success") {
                    this.show_success("Compliance thresholds saved.");
                    document.getElementById("close_threshold_modal_btn")?.click();
                } else {
                    this.show_error(response.data?.message || "Failed to save thresholds.");
                }
            } catch (error) {
                this.show_error(
                    "Failed to save thresholds: " + (error.response?.data?.message || error.message)
                );
            } finally {
                this.threshold_saving = false;
            }
        },

    },
    mounted() {
        this.get_form_sections_with_sections();
    },
};